import { useState } from 'react';
import { Plus, Search, MoreHorizontal, Edit, Eye, UserX, UserCheck } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable, Column } from '@/components/common/DataTable';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { formatCurrency } from '@/lib/utils';
import { 
  Employee, 
  EmploymentStatus, 
  EmploymentType,
  EMPLOYMENT_STATUSES,
  EMPLOYMENT_TYPES,
} from '@/types';
import { useEmployees, useReactivateEmployee, useTerminateEmployee } from '@/hooks/useEmployees';
import { useToast } from '@/components/ui/Toast';
import { Link } from 'react-router-dom';

const getStatusBadge = (status: EmploymentStatus) => {
  const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning' | 'info'> = {
    active: 'success',
    on_leave: 'warning',
    terminated: 'secondary',
  };
  const config = EMPLOYMENT_STATUSES.find((s) => s.value === status);
  return <Badge variant={variants[status] || 'default'}>{config?.label || status}</Badge>;
};

const getTypeLabel = (type: EmploymentType) => {
  const config = EMPLOYMENT_TYPES.find((t) => t.value === type);
  return config?.label || type;
};

export function EmployeesPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<EmploymentStatus | 'all'>('all');
  const [typeFilter, setTypeFilter] = useState<EmploymentType | 'all'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  
  const { toast } = useToast();
  const { data: employeesData, isLoading } = useEmployees({
    search: searchQuery,
    status: statusFilter === 'all' ? undefined : statusFilter,
    type: typeFilter === 'all' ? undefined : typeFilter,
    page: currentPage,
    limit: pageSize,
  });
  
  const terminateEmployee = useTerminateEmployee();
  const reactivateEmployee = useReactivateEmployee();

  const employees = employeesData?.data || [];
  const totalItems = employeesData?.meta?.total || 0;

  const handleTerminate = async (id: string) => {
    if (!confirm('Are you sure you want to terminate this employee?')) return;
    
    try {
      await terminateEmployee.mutateAsync({ id, endDate: new Date().toISOString().split('T')[0] });
      toast({
        title: 'Success',
        description: 'Employee terminated successfully',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to terminate employee',
        variant: 'destructive',
      });
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await reactivateEmployee.mutateAsync(id);
      toast({
        title: 'Success',
        description: 'Employee reactivated successfully',
        variant: 'success',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to reactivate employee',
        variant: 'destructive',
      });
    }
  };

  const columns: Column<Employee>[] = [
    {
      key: 'code',
      header: 'Code',
      sortable: true,
      cell: (row) => (
        <Link 
          to={`/payroll/employees/${row.id}`}
          className="font-medium text-aus-blue hover:underline"
        >
          {row.code}
        </Link>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      cell: (row) => `${row.firstName} ${row.lastName}`,
    },
    {
      key: 'employmentType',
      header: 'Type',
      sortable: true,
      cell: (row) => getTypeLabel(row.employmentType),
    },
    {
      key: 'employmentStatus',
      header: 'Status',
      sortable: true,
      cell: (row) => getStatusBadge(row.employmentStatus),
    },
    {
      key: 'payRate',
      header: 'Pay Rate',
      sortable: true,
      cell: (row) => (
        <span>
          {formatCurrency(row.payRate)}
          <span className="text-muted-foreground text-xs ml-1">
            /{row.payType === 'hourly' ? 'hr' : 'yr'}
          </span>
        </span>
      ),
    },
    {
      key: 'jobTitle',
      header: 'Job Title',
      sortable: true,
      cell: (row) => row.jobTitle,
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      cell: (row) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem asChild>
              <Link to={`/payroll/employees/${row.id}`} className="flex items-center cursor-pointer">
                <Eye className="mr-2 h-4 w-4" />
                View
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={`/payroll/employees/${row.id}?edit=true`} className="flex items-center cursor-pointer">
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </DropdownMenuItem>
            {row.employmentStatus === 'active' ? (
              <DropdownMenuItem 
                className="text-destructive focus:text-destructive flex items-center cursor-pointer"
                onClick={() => handleTerminate(row.id)}
              >
                <UserX className="mr-2 h-4 w-4" />
                Terminate
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem 
                className="flex items-center cursor-pointer"
                onClick={() => handleReactivate(row.id)}
              >
                <UserCheck className="mr-2 h-4 w-4" />
                Reactivate
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  // Calculate summary stats
  const activeCount = employees.filter(e => e.employmentStatus === 'active').length;
  const onLeaveCount = employees.filter(e => e.employmentStatus === 'on_leave').length;
  const terminatedCount = employees.filter(e => e.employmentStatus === 'terminated').length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Employees"
        description="Manage your employees and their payroll details"
        action={{
          label: 'Add Employee',
          onClick: () => window.location.href = '/payroll/employees/new',
          icon: <Plus className="h-4 w-4" />,
        }}
      />

      {/* Stats Summary */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="p-4 rounded-lg border">
          <p className="text-sm text-muted-foreground">Total Employees</p>
          <p className="text-2xl font-bold">{totalItems}</p>
        </div>
        <div className="p-4 rounded-lg border">
          <p className="text-sm text-muted-foreground">Active</p>
          <p className="text-2xl font-bold text-success">{activeCount}</p>
        </div>
        <div className="p-4 rounded-lg border">
          <p className="text-sm text-muted-foreground">On Leave</p>
          <p className="text-2xl font-bold text-warning">{onLeaveCount}</p>
        </div>
        <div className="p-4 rounded-lg border">
          <p className="text-sm text-muted-foreground">Terminated</p>
          <p className="text-2xl font-bold text-muted-foreground">{terminatedCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search employees..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select
          value={statusFilter}
          onValueChange={(value) => setStatusFilter(value as EmploymentStatus | 'all')}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {EMPLOYMENT_STATUSES.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                {status.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={typeFilter}
          onValueChange={(value) => setTypeFilter(value as EmploymentType | 'all')}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {EMPLOYMENT_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Data Table */}
      <DataTable
        data={employees}
        columns={columns}
        keyExtractor={(row) => row.id}
        isLoading={isLoading}
        totalItems={totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        emptyMessage="No employees found. Add your first employee to get started."
      />
    </div>
  );
}
